import { useRouteLoaderData, useSearchParams } from '@remix-run/react'
import type { Filter } from 'mongodb'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { aEmbed, useAtomValue } from '~/atoms'
import type { Listing } from '~/models'
import { objectToHash, sendEmbedMessage } from '~/utils'

export default function useEmbed() {
  const embed = useAtomValue(aEmbed)
  const { originalUrl } = useRouteLoaderData('root') as { originalUrl?: string }
  const [searchParams] = useSearchParams()
  const [isEmbed, setIsEmbed] = useState(
    searchParams.get('embed') || originalUrl?.includes('embed') ? true : false
  )

  useEffect(() => {
    try {
      const hasParam =
        searchParams.get('embed') || originalUrl?.includes('embed')
          ? true
          : false

      setIsEmbed(embed !== null || hasParam || window.self !== window.top)
    } catch (e) {
      setIsEmbed(true)
    }
  }, [embed])

  const embedFind = useMemo(() => {
    let find: Filter<Listing> = {}

    if (embed?.broker) {
      find['brokers.tid'] = embed.broker
    }

    if (embed?.find && Object.keys(embed.find).length > 0) {
      return {
        ...embed.find,
        ...find,
      }
    } else if (embed?.companies) {
      const ids = embed.companies.map(({ _id }) => _id)

      return {
        ...find,
        'brokers.company._id': { $in: ids },
      }
    }
  }, [embed])

  const updateHostPageURL = useCallback(
    (listing?: Listing['slug'], replace?: boolean) => {
      if (!isEmbed || embed?.options?.preventHostPageURLUpdate) return

      const hash =
        !listing && !embed?.broker
          ? undefined
          : objectToHash({
              listings: listing,
              brokers: embed?.broker,
            })

      sendEmbedMessage({
        type: 'update-listing-hash',
        hash,
        replace,
      })
    },
    [isEmbed, embed?.options?.preventHostPageURLUpdate, embed?.broker]
  )

  return {
    isEmbed,
    hostURL: embed?.hostURL,
    openLinksInNewTab: embed?.options?.openLinksInNewTab ?? isEmbed,
    website: embed?.website,
    embedFind,
    initialViewState: embed?.options.initialViewState,
    updateHostPageURL,
    isCompanyEmbed: embed?.companies && embed.companies.length > 0,
  }
}
